import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators';

import { TypedActivatedRouteSnapshot } from '../utils/route-utils';

const getRouteChangesCore = <
  TRouteData extends Record<string, any>,
  TKey extends keyof TRouteData,
  TResult,
>(
  router: Router,
  dataKey: TKey,
  transform: (routeData: TRouteData[TKey]) => TResult | null,
) =>
  router.events.pipe(
    filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
    startWith(null),
    // Get data
    map(() =>
      getRouteDataForKey<TRouteData, TKey>(
        router.routerState.snapshot.root as TypedActivatedRouteSnapshot<TRouteData>,
        dataKey,
      ),
    ),
    distinctUntilChanged(),
    // Transform data
    map(data => (data != null ? transform(data) : null)),
  );

const getRouteDataForKey = <TRouteData extends Record<string, any>, TKey extends keyof TRouteData>(
  route: TypedActivatedRouteSnapshot<any, Partial<TRouteData>, any>,
  key: TKey,
): TRouteData[TKey] | undefined => {
  const routesToCheck = [route];

  while (true) {
    const currentRoute = routesToCheck.shift();

    if (!currentRoute) {
      return undefined;
    }

    if (key in currentRoute.data) {
      return currentRoute.data[key];
    }

    routesToCheck.push(...currentRoute.children);
  }
};

export const getRouteChanges = <
  TRouteData extends Record<string, any>,
  TKey extends keyof Partial<TRouteData>,
  TResult,
>(
  router: Router,
  dataKey: TKey,
  transform: (routeData: TRouteData[TKey]) => TResult | null,
) => getRouteChangesCore(router, dataKey, transform).pipe(shareReplay(1));

export const getRouteChangesWith = <
  TRouteData extends Record<string, any>,
  TKey extends keyof Partial<TRouteData>,
  TResult,
>(
  router: Router,
  dataKey: TKey,
  transform: (routeData: TRouteData[TKey]) => TResult | null,
  inputChanges: Observable<TResult | null>,
) => {
  const routeChanges$ = getRouteChangesCore(router, dataKey, transform);

  return combineLatest([routeChanges$, inputChanges]).pipe(
    map(([routeValue, inputValue]) => inputValue ?? routeValue),
    shareReplay(1),
  );
};
